/**
 * CertificateCard — Phase 4B unified list card.
 *
 * Presentation hierarchy (Collector-first):
 *   1. Verified pill (top strip)
 *   2. Collector name        ← visual hero of the card
 *   3. Certificate number    ← label "Certificate Number" + mono value
 *   4. Album                 ← friendly display name
 *   5. Completion date
 *   6. Verified status footer
 *
 * The card renders as a link to `/certificates/{cert_number}` unless
 * `href` is overridden.
 *
 * Reads pure props only — no fetching. Safe on any surface (public registry,
 * MyCertificates, search results).
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ChevronRight, User } from "lucide-react";
import {
  friendlyAlbumName,
  coverTypeLabel,
  formatCertNumber,
  formatCompletionDate,
} from "@/lib/certificateFormat";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Neutral profile-photo placeholder. Renders as a solid square with
 * the collector's initials when we have a display name, otherwise
 * shows the profile icon. Never emits a broken-image icon.
 */
function AvatarPlaceholder({ dark, displayName, size = "w-24 h-24" }) {
  const initials = React.useMemo(() => {
    if (!displayName) return null;
    const parts = String(displayName).trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || "").join("") || null;
  }, [displayName]);
  return (
    <div
      className={
        "shrink-0 rounded-lg flex items-center justify-center overflow-hidden " +
        size + " " +
        (dark ? "bg-white/10 text-white/70" : "bg-[var(--panini-yellow-soft)] text-[var(--ink)]")
      }
      aria-hidden="true"
      data-testid="cert-card-avatar-placeholder"
    >
      {initials
        ? <span className="font-display font-bold text-lg md:text-xl">{initials}</span>
        : <User size={28} />}
    </div>
  );
}

/**
 * Square profile photo with graceful fallback to the placeholder on
 * image-load failure. Lazy-loaded and dimension-reserved so it does
 * not cause layout shift.
 */
function AvatarPhoto({ mediaUrl, dark, displayName, sizeClass }) {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return <AvatarPlaceholder dark={dark} displayName={displayName} size={sizeClass} />;
  }
  return (
    <img
      src={mediaUrl}
      alt=""
      loading="lazy" decoding="async"
      onError={() => setFailed(true)}
      className={
        "shrink-0 rounded-lg object-cover " + sizeClass + " " +
        (dark ? "border border-white/10" : "border-2 border-[var(--ink)]/10")
      }
      style={{ aspectRatio: "1 / 1" }}
      data-testid="cert-card-avatar-photo"
    />
  );
}

/**
 * @typedef {Object} CertificateCardProps
 * @property {string}  cert_number
 * @property {string}  [collector_name]
 * @property {string}  [album_slug]
 * @property {string}  [album_label]     (optional pre-computed override)
 * @property {"HC"|"SC"|string} [album_type]
 * @property {string}  [completion_date]
 * @property {boolean} [is_claimed]
 * @property {boolean} [verified]        defaults to true — every published cert is verified.
 * @property {string}  [href]            override link target
 * @property {"dark"|"light"} [theme]    default "dark" for the public registry
 * @property {string}  [testid]
 * @property {string}  [ownedByYouLabel] shows an "Owned by You" hint (MyCertificates)
 */

export default function CertificateCard({
  cert_number,
  collector_name,
  album_slug,
  album_label,
  album_type,
  completion_date,
  is_claimed,
  verified = true,
  href,
  theme = "dark",
  testid,
  ownedByYouLabel,
  owner_username,
  owner_display_name,
  owner_avatar_media_id,
  owner_avatar_updated_at,
  owner_profile_public,
}) {
  const number = formatCertNumber(cert_number);
  const linkTarget = href || `/certificates/${number}`;
  const albumName = album_label || friendlyAlbumName(album_slug);
  const cover = coverTypeLabel(album_type);
  const completed = formatCompletionDate(completion_date);
  const dark = theme === "dark";

  // Owner avatar wiring — mirrors the pattern used by the public
  // collector profile page: `/api/public/collectors/{username}/avatar`
  // with a cache-busting `v` param derived from `avatar_updated_at`.
  // Only linked, opted-in profiles reach the photo branch — the
  // registry payload sets `owner_avatar_media_id` to null otherwise.
  const publicSlug = owner_username;
  const hasAvatar = !!owner_avatar_media_id;
  const avatarUrl = hasAvatar
    ? `${API_BASE}/public/collectors/${encodeURIComponent(publicSlug || "")}/avatar` +
      `?v=${encodeURIComponent(owner_avatar_updated_at || "")}`
    : null;
  const clickableAvatar = owner_profile_public && !!publicSlug;
  const avatarSizeClass = "w-20 h-20 md:w-24 md:h-24";
  const avatarBlock = hasAvatar
    ? <AvatarPhoto mediaUrl={avatarUrl} dark={dark}
                   displayName={owner_display_name || collector_name}
                   sizeClass={avatarSizeClass} />
    : <AvatarPlaceholder dark={dark}
                          displayName={owner_display_name || collector_name}
                          size={avatarSizeClass} />;

  return (
    <Link
      to={linkTarget}
      data-testid={testid || `cert-card-${number}`}
      className={
        "group block rounded-2xl border p-6 transition-colors " +
        (dark
          ? "border-white/10 bg-white/[0.03] hover:border-[var(--gm-gold)]/50 hover:bg-white/[0.05]"
          : "border-2 border-[var(--ink)] bg-white shadow-[3px_3px_0_var(--ink)] hover:shadow-[5px_5px_0_var(--ink)]")
      }
    >
      {/* 1 — Verified pill */}
      <div className="flex items-center justify-between">
        <span
          data-testid={`cert-card-${number}-verified-pill`}
          className={
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold " +
            (dark
              ? "border border-[var(--gm-gold)]/40 text-[var(--gm-gold)] bg-[var(--gm-gold)]/8"
              : "border-2 border-emerald-900 text-emerald-900 bg-emerald-50")
          }
        >
          <ShieldCheck size={11} /> Official Verified Album
        </span>
        <ChevronRight
          size={16}
          className={dark ? "text-white/40 group-hover:text-[var(--gm-gold)]" : "text-[var(--smoke)] group-hover:text-[var(--panini-red)]"}
          aria-hidden
        />
      </div>

      {/* 2 — Collector row: name + right-side square profile photo */}
      <div className="mt-5 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className={"text-[10px] uppercase tracking-[0.24em] font-bold " + (dark ? "text-white/50" : "text-[var(--smoke)]")}>
            Collector
          </div>
          <div
            data-testid={`cert-card-${number}-collector`}
            className={
              "mt-1 font-display font-bold leading-[1.05] break-words " +
              (dark
                ? "text-white text-2xl md:text-3xl"
                : "text-[var(--ink)] text-2xl md:text-3xl")
            }
          >
            {collector_name || "Unnamed Collector"}
          </div>
          {ownedByYouLabel && (
            <div
              className={"mt-1 inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full " +
                (dark ? "bg-white/10 text-white/80" : "bg-[var(--panini-yellow-soft)] text-[var(--ink)]")}
              data-testid={`cert-card-${number}-owned-by-you`}
            >
              {ownedByYouLabel}
            </div>
          )}
        </div>
        {/* Right-side square profile photo. Only the photo (not the
            surrounding card) links to the public profile — otherwise
            the outer <Link> would swallow the click. */}
        {clickableAvatar ? (
          <Link
            to={`/collectors/${encodeURIComponent(publicSlug)}`}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0"
            aria-label={`Open ${owner_display_name || collector_name || "collector"} public profile`}
            data-testid={`cert-card-${number}-avatar-link`}
          >
            {avatarBlock}
          </Link>
        ) : (
          <div className="shrink-0" data-testid={`cert-card-${number}-avatar-static`}>
            {avatarBlock}
          </div>
        )}
      </div>

      {/* 3 — Certificate number */}
      <div className="mt-4">
        <div className={"text-[10px] uppercase tracking-[0.24em] font-bold " + (dark ? "text-white/50" : "text-[var(--smoke)]")}>
          Certificate Number
        </div>
        <div
          data-testid={`cert-card-${number}-number`}
          className={"font-mono font-bold mt-1 text-lg md:text-xl break-all " + (dark ? "text-[var(--gm-gold)]" : "text-[var(--panini-red)]")}
        >
          {number}
        </div>
      </div>

      {/* 4 — Album */}
      {albumName && (
        <div className="mt-4">
          <div className={"text-[10px] uppercase tracking-[0.24em] font-bold " + (dark ? "text-white/50" : "text-[var(--smoke)]")}>
            Album
          </div>
          <div
            data-testid={`cert-card-${number}-album`}
            className={"mt-1 text-base md:text-lg " + (dark ? "text-white/90" : "text-[var(--ink)] font-bold")}
          >
            {albumName}
            {cover ? <span className={dark ? " text-white/50" : " text-[var(--smoke)]"}> · {cover}</span> : null}
          </div>
        </div>
      )}

      {/* 5 — Completion date */}
      {completed && (
        <div className="mt-4">
          <div className={"text-[10px] uppercase tracking-[0.24em] font-bold " + (dark ? "text-white/50" : "text-[var(--smoke)]")}>
            Completed
          </div>
          <div
            data-testid={`cert-card-${number}-completion`}
            className={"mt-1 text-sm " + (dark ? "text-white/70" : "text-[var(--ink)]")}
          >
            {completed}
          </div>
        </div>
      )}

      {/* 6 — Verified footer */}
      <div
        className={
          "mt-5 pt-4 flex items-center justify-between border-t " +
          (dark ? "border-white/10" : "border-[var(--stone)]")
        }
      >
        <span
          className={
            "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold " +
            (verified
              ? dark
                ? "text-emerald-300"
                : "text-emerald-900"
              : dark
                ? "text-white/40"
                : "text-[var(--smoke)]")
          }
          data-testid={`cert-card-${number}-verified-status`}
        >
          <ShieldCheck size={11} /> {verified ? "Verified · Gil Maakaroun" : "Pending"}
        </span>
        {is_claimed && (
          <span
            className={"text-[10px] uppercase tracking-[0.2em] font-bold " +
              (dark ? "text-[var(--gm-gold)]" : "text-[var(--panini-red)]")}
            data-testid={`cert-card-${number}-claimed`}
          >
            Claimed
          </span>
        )}
      </div>
    </Link>
  );
}
